/**
 * Display rules for the Co-Living unit rows and room cards (Slice 3).
 *
 * Pure functions, no React, no fetching, so every rule is testable on its own
 * and the components stay presentational (CLAUDE.md Regel 48).
 *
 * NOTHING HERE DERIVES OCCUPANCY. `getRoomOccupancyStatus` is the one answer to
 * "is this room taken", and these helpers never look at a tenancy, a date or
 * `room.status`. They choose German words and format numbers.
 *
 * EVERY HELPER RETURNS null FOR "NOT KNOWN", never a zero or a guess. A room
 * with no recorded area must show no area at all — "0 m²" would be a fact the
 * data does not contain, and the reader cannot tell the two apart.
 *
 * ONE HELPER READS ZERO THE OTHER WAY ROUND, and it says so at its own site:
 * `formatUnitRunningCosts` formats a 0 rather than dropping it, because for
 * that contract a zero is a measurement and only `null` means "not known".
 * The room helpers cannot make that distinction — `normalizeRoom` defaults a
 * missing price to 0 — so they keep treating 0 as absence.
 */

import { formatCurrency } from "../apartments/apartmentsHelpers";

/**
 * Swiss-German number, so thousands read as 1'490 rather than 1,490.
 *
 * `toLocaleString` drops insignificant decimals on its own, so 18 stays "18"
 * while 20.5 stays "20.5" — no trailing ".0" to strip afterwards.
 */
function deCh(value) {
  return Number(value).toLocaleString("de-CH");
}

/**
 * Monthly rent, in the exact shape this page has always printed it.
 *
 * `normalizeRoom` defaults a missing price to 0 (api/adminData/rooms.js:156),
 * so 0 means "no price recorded" here just as it did in the room map, which
 * rendered an em dash for it. A falsy price therefore yields null and the
 * caller omits the figure rather than claiming the room is free of charge.
 */
export function formatRoomPrice(priceMonthly) {
  const n = Number(priceMonthly);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `CHF ${deCh(n)} / Mt.`;
}

/**
 * What the UNIT costs us in a normal month, as this page prints money.
 *
 * NULL AND ZERO ARE DIFFERENT ANSWERS HERE, and that is the whole reason this
 * is not `formatRoomPrice`. That helper drops any value `<= 0`, correctly: a
 * room price of 0 means "nothing recorded", because `normalizeRoom` defaults a
 * missing price to 0. The run-rate contract is the exact opposite.
 * `unit_running_costs_service` returns `null` when no CLASSIFIED recurring
 * source exists — "we know nothing" — and `0.0` when it found one that sums to
 * zero, which is a measurement. Collapsing the two is the bug the Einheiten
 * list carried for months: `landlord_rent_monthly_chf` had lost its write path
 * but kept `default=0`, so every unit printed a confident "CHF 0".
 *
 * So: a numeric 0 formats. Only `null`, `undefined`, an empty string or an
 * unparseable value returns null, and the caller renders the em dash.
 *
 * THE FRANC FIGURE IS NOT INVENTED HERE. `formatCurrency` is the formatter the
 * canonical running-costs presentation already uses — `apartments/UnitListRow`'s
 * `Money`, fed by `unitListModel`'s `runningCostsMonthlyChf` — so the unit
 * header and the Einheiten list round and group identically. The only thing
 * added is the `/ Mt.` period this page already writes on room rent above,
 * needed because a unit header has no column heading to carry the period the
 * way a list column does.
 *
 * "/ Mt." IS A RATE, NOT A CALENDAR MONTH. The contract is the CURRENT
 * recurring basis for a normal month: no lease proration, no one-time rows, no
 * month-to-date spend. That is why no month is ever named here — "Laufende
 * Kosten September" would be a different figure that nothing computes.
 */
export function formatUnitRunningCosts(monthlyChf) {
  if (monthlyChf == null || monthlyChf === "") return null;
  const n = Number(monthlyChf);
  if (!Number.isFinite(n)) return null;
  return `${formatCurrency(n)} / Mt.`;
}

/**
 * Floor area. Null unless a real positive number was recorded.
 */
export function formatRoomArea(sizeM2) {
  const n = Number(sizeM2);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${deCh(n)} m²`;
}

/**
 * Which floor the room is on.
 *
 * 0 is the ground floor and is a real answer, so the check is for "no value"
 * rather than for falsiness — `if (!floor)` would silently swallow EG.
 * Negative floors are outside the current contract and read as unknown rather
 * than inventing a basement vocabulary this product has never used.
 */
export function formatRoomFloor(floor) {
  if (floor === null || floor === undefined || floor === "") return null;
  const n = Number(floor);
  if (!Number.isInteger(n) || n < 0) return null;
  return n === 0 ? "EG" : `${n}. OG`;
}

/**
 * How large the flat-share is.
 *
 * Derived from the unit's FULL room count, never from the filtered view: a
 * status filter showing two of four rooms must not relabel the unit a "2er WG".
 * The size of the flat-share is a property of the unit, not of the current
 * filter.
 */
export function formatWgSize(totalRoomsInUnit) {
  const n = Number(totalRoomsInUnit);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${n}er WG`;
}

/**
 * The unit's address as two lines: street, then postcode and town.
 *
 * `getUnitAddressSubtitle` composes the same parts into ONE string
 * ("Street 17, 8004 Zürich"); the redesigned row stacks them, so the split has
 * to happen here. Field order mirrors that helper exactly
 * (utils/unitDisplayId.js:50-64) so the two cannot disagree about which field
 * is the street.
 *
 * Missing parts collapse rather than leaving stray commas or empty lines.
 */
export function splitUnitAddress(unit) {
  if (!unit) return { line1: null, line2: null };
  const street =
    String(unit.address_line1 ?? unit.street ?? unit.address ?? "").trim();
  const postcode = String(unit.postal_code ?? unit.zip ?? "").trim();
  const town = String(unit.city ?? unit.place ?? "").trim();
  const line2 = [postcode, town].filter(Boolean).join(" ");
  return { line1: street || null, line2: line2 || null };
}

/** "1 Einheit" / "4 Einheiten" — the count beside the section title. */
export function formatUnitCount(count) {
  const n = Number(count) || 0;
  return n === 1 ? "1 Einheit" : `${n} Einheiten`;
}

/** The room's display name, matching what the room map showed. */
export function roomDisplayName(room, index = 0) {
  const name = String(room?.roomName || room?.name || "").trim();
  return name || `Zimmer ${index + 1}`;
}

/**
 * Share of the total, to ONE DECIMAL — or null when there is no total (OC3).
 *
 * WAS A WHOLE PERCENT, and that was the visible half of a cross-surface
 * disagreement: 2 of 3 rooms read "67%" here and "66.7%" on the unit detail
 * hero, the occupancy overview and the Co-Living dashboard. One decimal is now
 * the single rule; `utils/occupancyPercent.js` holds the contract and the
 * reasoning.
 *
 * A TOTAL OF ZERO RETURNS NULL, not 0. It used to answer 0 "rather than NaN or
 * Infinity", which kept the DOM clean but stated something false: a ring at 0 %
 * looks exactly like a fully vacant population, and a population of nothing is
 * not vacant. Callers draw no ring and print no percentage for null — the KPI
 * grid already branches on `typeof percent === "number"` for exactly this.
 *
 * A NON-NUMERIC VALUE STILL ANSWERS 0. That is deliberate and unchanged: the
 * ring has to draw something, and `unitOccupancyPercent` below is the variant
 * that refuses instead (see its own note, and UI2-5b).
 */
export function toPercent(value, total) {
  const t = Number(total);
  if (!Number.isFinite(t) || t <= 0) return null;
  const v = Number(value);
  if (!Number.isFinite(v)) return 0;
  // One decimal, kept as a NUMBER so the ring can still compute an arc from it.
  // The string form (and its trailing ".0") belongs to `occupancyPercentText`.
  return Math.round((v / t) * 1000) / 10;
}

/**
 * How much of a unit is lived in right now, as a whole percent — or null.
 *
 * RESERVED IS NOT OCCUPIED. A reserved room has nobody in it today; it is a
 * promise about a tenancy that has not started. Folding it in here would let a
 * unit with four reservations and no tenants read as fully let, which is the
 * opposite of what this number is for. The badge row beside it goes on stating
 * both figures separately, so the reservations are not hidden by this choice —
 * they are simply not part of "belegt".
 *
 * A UNIT WITH NO ROOMS HAS NO OCCUPANCY, so this returns null rather than 0.
 * Zero percent is a measurement ("four rooms, none of them taken"); a unit
 * whose rooms are not recorded has produced no measurement at all, and printing
 * 0 % would put those two side by side in the same column looking identical.
 * Same rule as every other helper in this file, for the same reason. Anything
 * that is not a finite number is unknown in exactly the same way — which is
 * where this parts company with `toPercent`, which answers "what share is
 * this" for a ring that has to draw something either way.
 *
 * IT DEMANDS REAL NUMBERS, unlike its neighbours above. They format values that
 * arrive off the wire, where "18" is a legitimate way to say 18; these two
 * arrive from `AdminRoomsPage`'s own `.length` and filter counts and are always
 * numbers already. Coercing instead of checking would be actively harmful here,
 * because `Number(null)`, `Number("")`, `Number(false)` and `Number([])` are
 * all 0 — so a count that went missing would print as an authoritative "nobody
 * lives here" rather than as the em dash it is. `typeof` is what keeps
 * "measured zero" and "not measured" apart, and it is the whole point of this
 * helper.
 *
 * NO CLAMP. `occupied` and `total` come from one partition of one room list, so
 * `occupied <= total` holds structurally and a clamp could only ever hide a
 * fault in that partition — CLAUDE.md-Regel: no silent clamp without a rule
 * that asks for one.
 */
export function unitOccupancyPercent(occupied, total) {
  if (typeof total !== "number" || !Number.isFinite(total) || total <= 0) return null;
  if (typeof occupied !== "number" || !Number.isFinite(occupied)) return null;
  return toPercent(occupied, total);
}
